

/**
 * A row of tabs over one region.
 *
 * Keyboard behaviour comes from `role="tab"` plus real buttons: arrow keys are
 * deliberately not intercepted, because these tabs are a filter over a list
 * rather than a set of pages, and swallowing arrow keys would break scrolling
 * the list underneath.
 */
export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
  label,
}: {
  value: T;
  onChange: (next: T) => void;
  tabs: { value: T; label: string; count?: number }[];
  /** Names the group for a screen reader — say what is being filtered. */
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label} className="flex flex-wrap gap-1 border-b border-line">
      {tabs.map((tab) => {
        const selected = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(tab.value)}
            className={`-mb-px flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-sm font-semibold
              transition duration-200 ease-out
              focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600
              ${
                selected
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-500 hover:border-line hover:text-ink'
              }`}
          >
            {tab.label}
            {/* Zero is worth showing — "0 waiting" is the answer someone came
                to this tab for, and hiding it reads as "not loaded yet". */}
            {tab.count !== undefined && (
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                  selected ? 'bg-brand-50 text-brand-700' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
